import type { UsageType, UserUsage } from '@/common/db-typebox';
import { useTreatyClient } from '@/lib/client';
import { Card, Progress, Table, Tag, Typography } from 'antd';
import { useEffect, useState } from 'react';

const { Title } = Typography;

// Map usage types to display names
const usageTypeLabels: Record<UsageType, string> = {
  add_wine: 'Add Wine',
  add_menu: 'Add Menu',
  WineDishSimpleRecommendation: 'Simple Wine-Dish Recommendation',
  WineDishAnalysis: 'Wine-Dish Analysis',
  WineDishRecommendation: 'Wine-Dish Recommendation',
  WineSearch: 'Wine Search',
  WineAnalysis: 'Wine Analysis',
  WineProduct: 'Wine Product',
  MenuAnalysis: 'Menu Analysis',
};

export const UsagePage = () => {
  const [loading, setLoading] = useState(false);
  const [usageRecords, setUsageRecords] = useState<UserUsage[]>([]);
  const client = useTreatyClient();

  useEffect(() => {
    const fetchUsage = async () => {
      try {
        setLoading(true);
        const response = await client.api.user_usage.me.get();
        if (response.data?.success) {
          setUsageRecords(response.data.data);
        }
      } catch (error) {
        console.error('Failed to fetch usage records:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchUsage();
  }, [client]);

  const columns = [
    {
      title: 'Service',
      dataIndex: 'usage_type',
      key: 'usage_type',
      render: (type: UsageType) => (
        <Tag color="blue">{usageTypeLabels[type]}</Tag>
      ),
    },
    {
      title: 'Usage',
      key: 'usage',
      render: (record: UserUsage) => (
        <div className="space-y-1">
          <div className="flex justify-between text-sm">
            <span>{record.usage_count}</span>
            {record.usage_limit != null && <span>of {record.usage_limit}</span>}
          </div>
          {record.usage_limit != null && (
            <Progress
              percent={Math.round(
                (record.usage_count / record.usage_limit) * 100,
              )}
              size="small"
              status={
                record.usage_count >= record.usage_limit
                  ? 'exception'
                  : 'active'
              }
            />
          )}
        </div>
      ),
    },
    {
      title: 'Last Used',
      dataIndex: 'last_used_at',
      key: 'last_used_at',
      render: (date: string | null) =>
        date ? new Date(date).toLocaleString() : 'Never',
    },
    {
      title: 'Reset Date',
      dataIndex: 'reset_at',
      key: 'reset_at',
      render: (date: string | null) =>
        date ? new Date(date).toLocaleString() : 'N/A',
    },
    {
      title: 'Created',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date: string) => new Date(date).toLocaleString(),
    },
  ];

  return (
    <div className="p-8">
      <div className="space-y-6">
        <Card>
          <Title level={2}>Usage Records</Title>
          <Table
            columns={columns}
            dataSource={usageRecords}
            rowKey="id"
            loading={loading}
            pagination={{
              pageSize: 10,
              showSizeChanger: true,
              showTotal: (total) => `Total ${total} records`,
            }}
          />
        </Card>
      </div>
    </div>
  );
};
